import {
  Link,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  Box,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import {
  ApiResponse_GetAll_Tenant,
  useGetAllTenants,
} from '@/api/endpoints/tenant/useGetAllTenants';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { getFromNow } from '@/utils/date';
import { formatPhoneNumber } from '@/utils/phone';
import { getResolvedRoutePath } from '@/utils/router';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import TenantDeleteModal from '@/components/tenant/TenantDeleteModal';
import TenantRetrieveModal from '@/components/tenant/TenantRetrieveModal';


const TABLE_HEAD = [
  { id: 'full_name', label: 'Name', sortable: true, width: '30%' },
  { id: 'contacts', label: 'Contact Info', sortable: false, width: '45%' },
  { id: 'archived_at', label: 'Archived At', sortable: true, width: '15%' },
];

type Order = 'asc' | 'desc';
type Tenant = ApiResponse_GetAll_Tenant[0];

function descendingComparator(a: Tenant, b: Tenant, orderBy: keyof Tenant) {
  if (orderBy === 'full_name') {
    const aValue = String(a[orderBy] ?? '').toLowerCase();
    const bValue = String(b[orderBy] ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (orderBy === 'archived_at') {
    const aDate = new Date(String(a[orderBy] ?? '')).getTime();
    const bDate = new Date(String(b[orderBy] ?? '')).getTime();
    return bDate - aDate;
  }
  return 0;
}

function getComparator(order: Order, orderBy: keyof Tenant) {
  return order === 'desc'
    ? (a: Tenant, b: Tenant) => descendingComparator(a, b, orderBy)
    : (a: Tenant, b: Tenant) => -descendingComparator(a, b, orderBy);
}


const UnitViewPastTenantListSubpage: React.FC = () => {
  const { id } = useParams<ExtractParams<PMPRoutes.unitView>>();

  const [tenantToRetrieve, setTenantToRetrieve] = useState<
    ApiResponse_GetAll_Tenant[0] | null
  >(null);
  const [tenantToDelete, setTenantToDelete] = useState<
    ApiResponse_GetAll_Tenant[0] | null
  >(null);
  const [isTenantRetrieveModalOpen, setIsTenantRetrieveModalOpen] = useState(false);
  const [isTenantDeleteModalOpen, setIsTenantDeleteModalOpen] = useState(false);

  const theme = useTheme();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Tenant>('archived_at');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (property: keyof Tenant) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const getAllTenantsQuery = useGetAllTenants({ unit_id: Number(id), is_archived: true });

  const filteredData = useMemo(() => {
    let filtered = getAllTenantsQuery.data || [];
  
    if (filterName) {
      filtered = filtered.filter(item =>
        item.full_name.toLowerCase().includes(filterName.toLowerCase())
      );
    }
  
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllTenantsQuery.data, filterName, order, orderBy]);
  
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const onRetrieveTenantClick = useCallback((tenant: ApiResponse_GetAll_Tenant[0]) => {
    setTenantToRetrieve(tenant);
    setIsTenantRetrieveModalOpen(true);
  }, [setTenantToRetrieve, setIsTenantRetrieveModalOpen]);

  const onDeleteTenantClick = useCallback((tenant: ApiResponse_GetAll_Tenant[0]) => {
    setTenantToDelete(tenant);
    setIsTenantDeleteModalOpen(true);
  }, [setTenantToDelete, setIsTenantDeleteModalOpen]);

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search tenants..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      bgcolor: theme.palette.secondary[200],
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof Tenant)}
                      >
                        <Typography
                          sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            color: theme.palette.secondary.darker,
                          }}
                        >
                          {headCell.label}
                        </Typography>
                      </TableSortLabel>
                    
                    ) : (
                      <Typography
                        fontWeight="bold"
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          color: theme.palette.secondary.darker,
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ width: '50px', bgcolor: theme.palette.secondary[200] }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllTenantsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllTenantsQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tenants found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllTenantsQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => (
                  <TableRow
                    key={String(row.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '25%' }}>
                      <Link
                        href={getResolvedRoutePath(PMPRoutes.tenantView, { id: String(row.id) })}
                        sx={{
                          color: theme.palette.secondary.darker,
                          textDecoration: 'none',
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {row.full_name}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ width: '35%' }}>
                      <Stack direction="column" spacing={1.25}>
                        {row.contacts.map(contact => (
                          <Stack direction="row" spacing={1} alignItems="center" key={contact.id}>
                            {!!contact.email && (
                              <Typography variant="body3" color="secondary.darker">
                                {contact.email}
                              </Typography>
                            )}
                            {!!contact.email && !!contact.phone && (
                              <Typography variant="body3" color="secondary.darker">
                                •
                              </Typography>
                            )}
                            {!!contact.phone && (
                              <Typography variant="body3" color="secondary.darker">
                                {formatPhoneNumber(contact.phone)}
                              </Typography>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <Typography variant="body3" color="secondary.darker">
                        {row.archived_at ? getFromNow(new Date(row.archived_at)) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: '50px' }}>
                      <RowActionsMenu
                        items={[
                          {
                            label: 'Retrieve',
                            onClick: () => onRetrieveTenantClick(row),
                          },
                          {
                            label: 'Delete',
                            onClick: () => onDeleteTenantClick(row),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isTenantRetrieveModalOpen && tenantToRetrieve && (
        <TenantRetrieveModal
          onClose={() => {
            setIsTenantRetrieveModalOpen(false);
            setTenantToRetrieve(null);
          }}
          tenant={tenantToRetrieve}
        />
      )}

      {isTenantDeleteModalOpen && tenantToDelete && (
        <TenantDeleteModal
          onClose={() => {
            setIsTenantDeleteModalOpen(false);
            setTenantToDelete(null);
          }}
          tenant={tenantToDelete}
        />
      )}
    </Stack>
  );
};

export default UnitViewPastTenantListSubpage;
